import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserRound } from "lucide-react";
import { updateUser } from "../../../api/user/userApi";
import type { UserResponse } from "../../../types/user/user.model";
import { useAuthStore } from "../../../store/useAuthStore";
import { NameField, SubmitButton } from "./fields";

const SKIP_KEY = "askName:skipped";

/**
 * Accounts created through the email link (and some social logins) have no
 * name. Ask once, right after sign-in; "Later" hides it for this session.
 */
export function AskNameDialog() {
  const { t } = useTranslation();
  const { id, name, isAuthenticated, setUserData } = useAuthStore();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [skipped, setSkipped] = useState(() => {
    try {
      return sessionStorage.getItem(SKIP_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (!isAuthenticated || id == null || (name ?? "").trim() || skipped) return null;

  const skip = () => {
    try {
      sessionStorage.setItem(SKIP_KEY, "1");
    } catch {
      /* ignore */
    }
    setSkipped(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (v.length < 2) {
      setError(t("authFlow.nameRequired", "Введите имя"));
      return;
    }
    setBusy(true);
    try {
      // The endpoint accepts a partial profile — only the name changes.
      await updateUser({ userId: id, userRequest: { name: v } as UserResponse });
      setUserData({ name: v } as UserResponse);
    } catch {
      setError(t("authFlow.errors.unknown", "Что-то пошло не так. Попробуйте ещё раз."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-end justify-center bg-slate-900/40 backdrop-blur-sm sm:items-center sm:p-6" role="dialog" aria-modal="true">
      <form
        onSubmit={save}
        noValidate
        className="w-full rounded-t-3xl bg-white px-6 pb-8 pt-6 shadow-2xl sm:max-w-md sm:rounded-3xl"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px) + 24px, 32px)" }}
      >
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
          <UserRound size={24} />
        </div>
        <h2 className="text-xl font-bold text-gray-900">{t("authFlow.askNameTitle", "Как вас зовут?")}</h2>
        <p className="mt-1 mb-5 text-sm text-gray-500">
          {t("authFlow.askNameText", "Имя увидят покупатели и продавцы в ваших объявлениях.")}
        </p>
        <NameField
          label={t("authFlow.name", "Имя")}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(undefined);
          }}
          autoFocus
          enterKeyHint="done"
          error={error}
        />
        <div className="mt-5 flex flex-col gap-2">
          <SubmitButton busy={busy}>{t("authFlow.save", "Сохранить")}</SubmitButton>
          <button type="button" onClick={skip} className="h-11 text-[15px] font-medium text-gray-500 hover:text-gray-700">
            {t("authFlow.later", "Позже")}
          </button>
        </div>
      </form>
    </div>
  );
}
